// ADR 8.9.26p §1/§2 — the page every unmatched URL lands on.
//
// `public/_redirects` (5.9.26k §3) rewrites `/*` to `index.html`, so a typo, a
// stale bookmark or an API path a reader assumed was a page all reach the SPA.
// Until this file existed they reached React Router's built-in error UI, which
// in the production build is two words of English and a status number.
//
// It lives in `app/` rather than `features/<domain>/` on purpose: it belongs to
// no domain, it is the route table's companion, and it sits beside `Shell` and
// `router.tsx`.
//
// No new copy. Both keys are already translated in all 31 catalogues, and the
// link is the exercise's plan-complete anchor verbatim (`PlayPage.tsx` then,
// `features/exercise/ExercisePanel.tsx` since 16.9.26o) — same key, same
// destination, same class string — because the web already has exactly one way
// back and shipping two different words for it would be worse than shipping the
// imperfect one twice. ADR 5.9.26t §1 settled that trade for `ExpiredLinkPage`
// and this is the same state.

import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { emitRouteNotFound } from '../layout/analytics'
import { Anchor } from '../ui/Anchor'
import { PageFrame } from '../layout/frame/PageFrame'

export function NotFoundPage() {
  const { t } = useTranslation()
  // ADR 9.9.26b §1 — `useLocation()`, never the global `location`: under
  // `createMemoryRouter` jsdom's `window.location` never leaves `/`, so a bare
  // `location.pathname` would emit `/` for every case and the suite would pass
  // while measuring nothing. Reading the hook's `pathname` field also makes the
  // `search`/`hash` exclusion structural rather than a strip.
  const { pathname } = useLocation()

  // Keyed on `pathname`, not `[]`: the catch-all keeps this page mounted across
  // `/features` → `/pricing` — same matched route, no unmount — so an empty
  // dependency array would count one 404 where a reader hit two. StrictMode's
  // development-only double invoke needs no latch here; `mountAt` does not use
  // it and a doubled dev event is not production data.
  useEffect(() => {
    emitRouteNotFound(pathname)
  }, [pathname])

  return (
    <PageFrame width="form" as="section" className="space-y-3 py-8" data-testid="not-found">
      <h2 className="text-xl font-semibold">{t('error_not_found')}</h2>
      <Anchor to="/plans">{t('plan_complete_back_to_lessons')}</Anchor>
    </PageFrame>
  )
}
